import React, { useCallback, useState } from 'react';
import _get from 'lodash/get';
import _omit from 'lodash/omit';
import _isEmpty from 'lodash/isEmpty';
import { useHistory, useLocation } from 'react-router';
import { useDispatch } from 'react-redux';
import makeSourceCreateBaseUrl from 'pages/Directory/helpers/makeSourceCreateBaseUrl';
import useAnalytics from 'helpers/useAnalytics';
import makeGetMappingsAttributes from 'fetches/makeGetMappingsAttributes';
import { useData } from 'resift';
import makeValidateMappings from 'fetches/makeValidateMappings';
import makeGetLatestSourceFile from 'pages/Source/fetches/makeGetLatestSourceFile';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Backdrop from '@material-ui/core/Backdrop';
import CircularProgress from '@material-ui/core/CircularProgress';
import MappingsForm from 'pages/Directory/MappingsForm';
import MappingsCard from 'pages/Source/components/MappingsCard';
import MappingsSummary from 'components/Mappings/MappingsSummary';

import SourceType from 'types/Source';
import MappingProps, * as MappingTypes from 'types/Mapping';
import {
  MAP_ATTRIBUTES_URL,
  DATA_SETTINGS_URL,
  UPLOAD_CSV_URL,
  CSV_WIZARD_URL,
  CSV_WIZARD_UPLOAD_URL,
} from 'pages/Directory/consts';
import { HOME_URL, DIRECTORIES_URL, SOURCE_URL } from 'components/Router/consts';
import { CSV_WIZARD_MAPPING_URL, CSV_SOURCE_TYPE_KEY } from 'pages/Directory/consts';

interface Props {
  sendBatchMappings: (newMappings: MappingProps[]) => void;
  source: SourceType;
  onSyncMappings?: () => void;
  sourceFile?: any;
}

function MappingsLayout({ sendBatchMappings, source, onSyncMappings, sourceFile }: Props) {
  const classes = useStyles();
  const getLatestDataCSVFile = makeGetLatestSourceFile(source.id, 'data');
  const _lastDataCsv = useData(getLatestDataCSVFile);
  const lastDataCsv = sourceFile || _lastDataCsv;
  const validateMappings = makeValidateMappings(source.id);
  const validationResponse = useData(validateMappings) as MappingTypes.ValidateMappingsResponse;

  const [batchMappings, setBatchMappings] = useState() as any;
  const [loading, setLoading] = useState(false);
  const analytics = useAnalytics();
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation();
  const isOnDirWizard = location.pathname.includes(MAP_ATTRIBUTES_URL);
  const isOnCsvUploadWizard = location.pathname.endsWith(CSV_WIZARD_MAPPING_URL);
  const [dirty, setDirty] = useState(isOnDirWizard);

  const baseUrl = makeSourceCreateBaseUrl(source.directoryId, source.id);
  const getMappingsAttributes = makeGetMappingsAttributes(source.id);

  const handleUploadNewCsv = useCallback(() => {
    const uploadCsvPath = isOnDirWizard
      ? `${baseUrl}${UPLOAD_CSV_URL}`
      : `${HOME_URL}${DIRECTORIES_URL}/${source.directoryId}${SOURCE_URL}/${source.id}${CSV_WIZARD_URL}${CSV_WIZARD_UPLOAD_URL}`;

    history.push(uploadCsvPath);
    window.location.reload();
  }, [source.id]);

  const setMappings = useCallback((newMappings: any) => {
    setBatchMappings(newMappings);
  }, []);

  const handleSubmit = useCallback(async () => {
    setDirty(false);
    setLoading(true);

    if (!_isEmpty(batchMappings)) {
      // (note - pearl) Remove `isCustom`, `_referenceId`, and `validation` keys,
      // which are only needed for FE
      const cleanedUpBatchMappings = batchMappings.map((m: any) => {
        return m.type === 'destroy'
          ? m
          : {
              ...m,
              data: _omit(m.data, ['isCustom', '_referenceId', 'validation']),
            };
      });

      await sendBatchMappings(cleanedUpBatchMappings);
    }

    setDirty(false);
    analytics.track('Mappings Saved', {
      category: 'AD - Directories',
    });

    if (isOnDirWizard) {
      analytics.track('Wizard - Mappings Step Completed', {
        category: 'AD - Directories',
        label: source.type,
      });
    }

    if (isOnCsvUploadWizard) {
      onSyncMappings && (await onSyncMappings());
    } else {
      setTimeout(() => {
        dispatch(getMappingsAttributes());
      }, 3000);

      const pushUrl = isOnDirWizard
        ? `${baseUrl}${DATA_SETTINGS_URL}`
        : `${HOME_URL}${DIRECTORIES_URL}/${source.directoryId}${SOURCE_URL}/${source.id}`;

      setTimeout(() => {
        history.push(pushUrl);
      }, 4000);
    }
  }, [batchMappings]);

  return (
    <div className={classes.root}>
      <div className={classes.mappingCards}>
        <MappingsCard source={source} />
        <MappingsForm source={source} setMappings={setMappings} dirty={dirty} setDirty={setDirty} />
      </div>
      {validationResponse && (
        <MappingsSummary
          className={classes.mappingSummary}
          validationResponse={validationResponse}
          csvFileName={_get(lastDataCsv, 'name')}
          onSubmit={handleSubmit}
          dirty={dirty}
          onUploadNewCsv={handleUploadNewCsv}
          isCsvSource={source.type === CSV_SOURCE_TYPE_KEY}
        />
      )}
      <Backdrop className={classes.backdrop} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </div>
  );
}

const useStyles = makeStyles(theme => ({
  root: { display: 'flex' },
  mappingCards: {
    flex: 2,
    marginRight: theme.spacing(2),
  },
  mappingSummary: {
    flex: 1,
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: theme.palette.common.white,
  },
}));

export default MappingsLayout;
